import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Alert, Spinner } from "react-bootstrap";
import "./epaper.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const EPaper = () => {
    const [papers, setPapers] = useState([]);
    const [selectedPaper, setSelectedPaper] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checkingPdf, setCheckingPdf] = useState(false);
    const [pdfError, setPdfError] = useState("");
    const pdfUrl = selectedPaper
        ? selectedPaper.file.startsWith("http")
            ? selectedPaper.file
            : `${API_URL}${selectedPaper.file}`
        : "";

    // 🔥 FETCH LATEST EPAPER FIRST
    useEffect(() => {
        const loadEpaper = async () => {
            setLoading(true);
            try {
                const latestRes = await fetch(`${API_URL}/api/epaper/latest`);
                const latestData = await latestRes.json();

                if (latestData && latestData.file) {
                    setSelectedPaper(latestData);
                }

                const listRes = await fetch(`${API_URL}/api/epaper`);
                const listData = await listRes.json();
                setPapers(listData || []);

                if (!latestData || !latestData.file) {
                    if (listData && listData.length > 0) {
                        setSelectedPaper(listData[0]);
                    }
                }
            } catch (err) {
                console.error("API Error:", err);
            } finally {
                setLoading(false);
            }
        };

        loadEpaper();
    }, []);

    useEffect(() => {
        if (selectedPaper) {
            setPdfError("");
            setCheckingPdf(true);
            fetch(pdfUrl, { method: "HEAD", cache: "no-store" })
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Unable to load the selected ePaper.");
                    }
                })
                .catch((err) => {
                    console.error("ePaper file load error:", err);
                    setPdfError(
                        "Cannot load the selected ePaper. Please try again later or open it directly."
                    );
                })
                .finally(() => setCheckingPdf(false));
        }
    }, [selectedPaper, pdfUrl]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            const key = e.key?.toLowerCase();
            if ((e.ctrlKey || e.metaKey) && ["p", "s", "c", "u"].includes(key)) {
                e.preventDefault();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    return (
        <Container fluid className="epaper-wrapper">

            {/* 📰 HEADER */}
            <div className="epaper-header">
                <h2>🗞 ePaper Archive</h2>

                {/* 📅 DATE SELECT */}
                <Form.Select
                    className="date-select"
                    value={selectedPaper?.date || ""}
                    onChange={(e) =>
                        setSelectedPaper(
                            papers.find((p) => p.date === e.target.value)
                        )
                    }
                    disabled={loading || papers.length === 0}
                >
                    {loading && papers.length === 0 ? (
                        <option>Loading ePaper dates...</option>
                    ) : papers.length > 0 ? (
                        papers.map((p) => (
                            <option key={p.date} value={p.date}>
                                {p.date}
                            </option>
                        ))
                    ) : selectedPaper ? (
                        <option value={selectedPaper.date}>{selectedPaper.date}</option>
                    ) : (
                        <option>No ePaper available</option>
                    )}
                </Form.Select>
            </div>

            { !selectedPaper ? (
                <Row className="py-5">
                    <Col className="text-center">
                        {loading ? (
                            <>
                                <Spinner animation="border" />
                                <div className="mt-3">Loading ePaper page...</div>
                            </>
                        ) : (
                            <Alert variant="info">
                                No ePaper is available yet. Please check back later.
                            </Alert>
                        )}
                    </Col>
                </Row>
            ) : (
                <Row>

                    <Col
                        md={12}
                        className="viewer-panel"
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        {checkingPdf && (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-3">Loading ePaper content...</div>
                            </div>
                        )}

                        {pdfError && !checkingPdf && (
                            <Alert variant="warning">
                                {pdfError}
                                <div className="mt-2">
                                    <a
                                        href={pdfUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Open ePaper in a new tab
                                    </a>
                                </div>
                            </Alert>
                        )}

                        {!checkingPdf && pdfUrl && !pdfError ? (
                            <object
                                title="ePaper Viewer"
                                data={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                type="application/pdf"
                                className="epaper-iframe"
                            >
                                <div className="text-center py-5">
                                    <p>PDF preview is not available in your browser.</p>
                                    <a href={pdfUrl} target="_blank" rel="noreferrer">
                                        Open ePaper in a new tab
                                    </a>
                                </div>
                            </object>
                        ) : null}

                        {!checkingPdf && !pdfUrl && (
                            <div className="text-center py-5">Viewer is not available</div>
                        )}
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default EPaper;